import { FC } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import {
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  Button,
  Box,
  Tooltip
} from '@mui/material';
import { ExpandMore, Delete, AttachFile, ChatBubble } from '@mui/icons-material';
import { resolveObjectField, truncString } from '../../../../helpers';
import { IPunchlistTodoItem } from '../../../../models';
import { theme } from '../../../../styles/theme';

interface IMobilePunchlistTodoList {
  original: IPunchlistTodoItem;
  handleDelete?: (val: IPunchlistTodoItem) => void;
  handleTitleClick: (val: IPunchlistTodoItem) => void;
  fields: {
    name: string;
    accessor: string;
    Cell?: (value: { cell: { row: { original: IPunchlistTodoItem } } }) => JSX.Element;
  }[];
  includeDeleteInDetails?: boolean;
}

export const MobilePunchlistTodoList: FC<IMobilePunchlistTodoList> = ({
  handleDelete,
  handleTitleClick,
  fields,
  original,
  includeDeleteInDetails
}) => {
  const classes = useStyles({ includeDeleteInDetails });

  const hasFiles = original?.punchlistItemFiles?.length > 0;
  const hasComments = original?.hasComments;

  const deleteButton = handleDelete ? (
    <IconButton
      className={classes.deleteButton}
      color="default"
      onClick={e => {
        e.stopPropagation();
        handleDelete(original);
      }}
      size="large"
    >
      <Delete />
    </IconButton>
  ) : null;

  return (
    <Accordion className={classes.root}>
      <AccordionSummary
        className={classes.accordion}
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div className={classes.topPanelSummaryWrapper}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Button
              style={
                original.itemIsAssociatedToFwtOrChecklist
                  ? { color: theme.palette.error.main }
                  : null
              }
              sx={{
                textAlign: 'left',
                textTransform: 'none',
                justifyContent: 'flex-start',
                textDecoration: original.itemStatuses === 'Complete' ? 'line-through' : 'auto',
                padding: '0',
                flex: 1,
                minWidth: 'auto'
              }}
              onClick={e => {
                e.stopPropagation();
                handleTitleClick(original);
              }}
            >
              {truncString(original.description, 26)}
            </Button>
          </Box>
          <div className={classes.buttonsWrapper}>
            {(hasFiles || hasComments) && (
              <Box display="flex" gap={0.25}>
                {hasFiles && (
                  <Tooltip title="Files attached">
                    <AttachFile
                      sx={{ color: theme => theme.palette.secondary.main }}
                      fontSize="small"
                    />
                  </Tooltip>
                )}
                {hasComments && (
                  <Tooltip title="Comments posted">
                    <ChatBubble
                      sx={{ color: theme => theme.palette.secondary.main }}
                      fontSize="small"
                    />
                  </Tooltip>
                )}
              </Box>
            )}
            {!includeDeleteInDetails && deleteButton}
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        {fields.map((field: (typeof fields)[0], index) => {
          if (field.accessor === 'punchListItemDescription') {
            return (
              <div key={`${index}`} className={classes.panelSummaryWrapper}>
                <FormLabel
                  component="span"
                  className={clsx(classes.labelColor, classes.boldName, classes.subLabel)}
                >
                  {field?.name?.length > 1 ? `${field.name}:` : field.name}
                </FormLabel>
                <Typography component="span" variant="body2" style={{ paddingTop: '4px' }}>
                  {original.description}
                </Typography>
              </div>
            );
          }
          return (
            <div key={`${index}`} className={classes.panelSummaryWrapper}>
              <FormLabel
                component="span"
                className={clsx(classes.labelColor, classes.boldName, classes.subLabel)}
              >
                {field?.name?.length > 1 ? `${field.name}:` : field.name}
              </FormLabel>
              <Typography component="span" variant="body2" style={{ paddingTop: '4px' }}>
                {typeof field.accessor === 'string' && typeof field.Cell !== 'function'
                  ? !resolveObjectField(field.accessor, original)
                    ? '--'
                    : resolveObjectField(field.accessor, original)
                  : typeof field.Cell === 'function'
                  ? ''
                  : '--'}
                {typeof field.Cell === 'function'
                  ? field.Cell({ cell: { row: { original } } })
                  : null}
              </Typography>
            </div>
          );
        })}
        {includeDeleteInDetails && deleteButton}
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    display: 'flex',
    padding: '0.25rem 0',
    '&:first-child': { marginTop: 0 }
  },
  details: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    padding: '1.125rem 1rem',
    backgroundColor: theme.palette.grey[100]
  },
  deleteButton: (props: { includeDeleteInDetails?: boolean }) => ({
    padding: `2px 5px`,
    color: theme.palette.error.main,
    // if delete is in accordion summary position top right
    ...(props.includeDeleteInDetails && {
      position: 'absolute',
      right: theme.spacing(0.5)
    })
  }),
  buttonsWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  boldName: {
    fontWeight: 600
  },
  labelColor: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1)
  },
  subLabel: {
    fontSize: '0.875rem'
  }
}));
